import React from "react";
import BrandLogo from "../components/common/BrandLogo";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, LogOut, Sparkles, Clock,
  Users, Boxes, Megaphone, Home, Wallet, GraduationCap,
  BrainCircuit, FileText, Smartphone, LineChart,
} from "lucide-react";
import { useAuth } from "../lib/auth";

/**
 * Placeholder landing page for the EstateOS suite.
 * Shown after a successful login through the "EstateOS" panel of /admin/login.
 * Individual modules will be wired up separately.
 */

const MODULES = [
  {
    name: "EstateOS CRM",
    icon: Users,
    description: "Müşteri ilişkileri, lead yönetimi ve satış süreçleri.",
    href: "/estateos/crm",
    live: true,
  },
  {
    name: "EstateOS ERP",
    icon: Boxes,
    description: "Kaynak planlaması, tedarik zinciri ve operasyon yönetimi.",
  },
  {
    name: "EstateOS Marketing",
    icon: Megaphone,
    description: "Kampanya yönetimi, otomasyon ve içerik pazarlaması.",
  },
  {
    name: "EstateOS Portfolio",
    icon: Home,
    description: "Arsa, konut ve ticari portföyün tek merkezden yönetimi.",
  },
  {
    name: "EstateOS Finance",
    icon: Wallet,
    description: "Muhasebe, nakit akışı ve finansal raporlama.",
  },
  {
    name: "EstateOS Academy",
    icon: GraduationCap,
    description: "İş ortağı eğitimleri, sertifikasyon ve gelişim programları.",
  },
  {
    name: "EstateOS AI",
    icon: BrainCircuit,
    description: "Yatırım analizi, fiyat tahmini ve akıllı öneriler.",
  },
  {
    name: "EstateOS Docs",
    icon: FileText,
    description: "Sözleşme, tapu ve resmi belge yönetimi.",
  },
  {
    name: "EstateOS Mobile",
    icon: Smartphone,
    description: "Saha ekipleri için mobil uygulama ve WhatsApp Business.",
  },
  {
    name: "EstateOS Analytics",
    icon: LineChart,
    description: "Gerçek zamanlı KPI, satış ve pazarlama analitikleri.",
  },
];

const EstateOSHome = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try { await logout(); } catch { /* noop */ }
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="estateos-home">
      {/* Top bar */}
      <header className="border-b border-black/5 bg-white sticky top-0 z-40 backdrop-blur-md bg-white/90">
        <div className="container-x flex items-center justify-between py-5">
          <div className="flex items-center gap-4">
            <BrandLogo place="header" max={48} />
            <span className="hidden sm:inline-flex items-center gap-2 px-3 py-1 border border-brand-navy/15 bg-brand-navy/[0.03] text-[0.62rem] tracking-[0.28em] uppercase text-brand-navy font-medium">
              <Sparkles size={12} strokeWidth={1.5} /> EstateOS
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            {user?.email ? (
              <span className="hidden md:inline text-xs text-brand-medium">{user.email}</span>
            ) : null}
            <Link
              to="/"
              className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium hover:text-brand-navy inline-flex items-center gap-2"
              data-testid="estateos-home-link"
            >
              <ArrowLeft size={12} strokeWidth={1.5} /> Ana Site
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-navy hover:text-brand-primary-bright inline-flex items-center gap-2 border border-black/10 hover:border-brand-navy px-4 py-2 transition-colors"
              data-testid="estateos-logout"
            >
              <LogOut size={12} strokeWidth={1.5} /> Çıkış
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #22346B 0%, #101F4A 100%)" }}
      >
        <div className="absolute inset-0 grain pointer-events-none opacity-30" />
        <div className="container-x py-16 md:py-24 text-white relative">
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-brand-primary-bright" />
            <span className="text-[0.66rem] tracking-[0.36em] uppercase text-brand-primary-bright font-medium">
              Fırat Digital Ecosystem
            </span>
          </div>
          <h1
            className="font-serif leading-[1.02] tracking-tight"
            style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}
          >
            Fırat EstateOS
          </h1>
          <p className="mt-6 text-white/80 max-w-2xl leading-relaxed">
            Gayrimenkul geliştirme, satış, pazarlama, finans ve akademi
            operasyonlarını tek bir dijital ekosistem içinde yönetmek için
            tasarlanmış bütünleşik iş platformu.
          </p>
          <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 bg-white/10 border border-white/15 text-xs tracking-[0.24em] uppercase text-white/90 backdrop-blur-sm">
            <Clock size={14} strokeWidth={1.5} className="text-brand-primary-bright" />
            Modüller Yakında Devreye Alınacak
          </div>
        </div>
      </section>

      {/* Modules grid */}
      <section className="bg-brand-light">
        <div className="container-x py-16 md:py-24">
          <div className="mb-10 max-w-2xl">
            <div className="text-[0.66rem] tracking-[0.32em] uppercase text-brand-primary font-medium mb-3">
              Suite Modülleri
            </div>
            <h2
              className="font-serif text-brand-navy leading-tight"
              style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
            >
              10 Entegre Modül, Tek Platform
            </h2>
            <p className="mt-4 text-brand-medium leading-relaxed">
              Her modül bağımsız çalışır, ancak veriler EstateOS çekirdeğinde
              gerçek zamanlı olarak senkronize edilir.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              const inner = (
                <>
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-full bg-brand-navy text-white flex items-center justify-center group-hover:bg-brand-primary-bright transition-colors">
                      <Icon size={18} strokeWidth={1.5} />
                    </div>
                    {m.live ? (
                      <span className="text-[0.55rem] tracking-[0.24em] uppercase text-emerald-700 px-2 py-1 border border-emerald-200 bg-emerald-50">
                        Aktif
                      </span>
                    ) : (
                      <span className="text-[0.55rem] tracking-[0.24em] uppercase text-brand-medium px-2 py-1 border border-black/10">
                        Yakında
                      </span>
                    )}
                  </div>
                  <div className="mt-5 font-serif text-xl text-brand-navy leading-tight">
                    {m.name}
                  </div>
                  <p className="mt-3 text-sm text-brand-medium leading-relaxed">
                    {m.description}
                  </p>
                  {m.live ? (
                    <div className="mt-4 inline-flex items-center gap-2 text-[0.66rem] tracking-[0.28em] uppercase text-brand-primary font-medium">
                      Aç →
                    </div>
                  ) : null}
                </>
              );
              const cls = `group bg-white border p-6 md:p-7 transition-all hover:-translate-y-1 hover:shadow-[0_18px_50px_-24px_rgba(34,52,107,0.35)] ${m.live ? "border-brand-navy/30 hover:border-brand-navy cursor-pointer" : "border-black/5 hover:border-brand-navy"}`;
              return (
                <motion.div
                  key={m.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  data-testid={`estateos-module-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {m.live && m.href ? (
                    <Link to={m.href} className={cls}>{inner}</Link>
                  ) : (
                    <div className={cls}>{inner}</div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom callout */}
      <section className="bg-white">
        <div className="container-x py-12 md:py-16">
          <div className="border-s-2 border-brand-primary ps-6 max-w-3xl">
            <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-primary font-medium">
              Roadmap
            </div>
            <h3 className="font-serif text-brand-navy text-2xl md:text-3xl mt-3 leading-tight">
              EstateOS Suite şu an inşa aşamasındadır.
            </h3>
            <p className="mt-3 text-brand-medium leading-relaxed">
              Modüller birbiri ardına devreye alınacaktır. İlk olarak
              <span className="font-medium text-brand-navy"> CRM </span>ve
              <span className="font-medium text-brand-navy"> Portfolio </span>
              modülleri kullanıma açılacaktır.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EstateOSHome;
